<template>
  <div class="flex flex-col gap-2.5">
    <span class="text-[13px] font-semibold text-neutral-800 tracking-tight">{{ label }}</span>
    <div class="rater-track">
      <button
        v-for="opt in options"
        :key="opt.value"
        type="button"
        class="rater-cell"
        :class="{ 'is-selected': modelValue === opt.value, [`tone-${opt.value}`]: true }"
        @click="$emit('update:modelValue', opt.value)"
      >
        <span class="rater-emoji" aria-hidden="true">{{ opt.emoji }}</span>
        <span class="rater-label">{{ t(`feedback.modal.unhappy.rating.${opt.value}`) }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { CriterionRating } from '@/services/feedback.service';

defineProps<{
  label: string;
  modelValue: CriterionRating;
}>();

defineEmits<{
  (e: 'update:modelValue', value: CriterionRating): void;
}>();

const { t } = useI18n();

const options = [
  { value: 'bad' as const,     emoji: '👎' },
  { value: 'neutral' as const, emoji: '🫳' },
  { value: 'good' as const,    emoji: '👍' },
];
</script>

<style scoped>
.rater-track {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  padding: 4px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.rater-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 9px 6px;
  border-radius: 10px;
  border: none;
  background: transparent;
  color: var(--color-neutral-500);
  transition: background 0.22s ease, color 0.22s ease, transform 0.22s ease;
  cursor: pointer;
}

.rater-cell:hover {
  background: var(--color-warm);
  color: var(--color-neutral-700);
}

.rater-cell.is-selected {
  transform: translateY(-0.5px);
}

.rater-cell.is-selected.tone-bad    { color: var(--color-red-800); background-color: var(--color-red-100); }
.rater-cell.is-selected.tone-neutral{ color: var(--color-primary-700);  background-color: var(--color-primary-100); }
.rater-cell.is-selected.tone-good   { color: var(--color-green-900);  background-color: var(--color-green-100); }

.rater-emoji {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  font-size: 19px;
  line-height: 1;
  filter: grayscale(1);
  opacity: 0.75;
  transition: filter 0.22s ease, opacity 0.22s ease;
}

.rater-cell.is-selected .rater-emoji {
  filter: none;
  opacity: 1;
}

.rater-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: uppercase;
}
</style>
